/** The single place a GraphQL request is made. */

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { print } from 'graphql';

/** Same-origin in production (nginx proxies /graphql to the API). */
const ENDPOINT = import.meta.env['VITE_GRAPHQL_URL'] ?? '/graphql';

export class GraphQLError extends Error {
  constructor(message: string, readonly errors: unknown[]) {
    super(message);
    this.name = 'GraphQLError';
  }
}

/**
 * Runs a typed document and returns its data.
 *
 * Partial results are kept rather than thrown away: a page asks for
 * several root fields at once, and one @cypher lead failing should not
 * blank the whole dossier. The failure is still reported to the console
 * so it cannot pass unnoticed.
 */
export async function request<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables]
): Promise<TResult> {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query: print(document), variables: variables ?? {} }),
  });

  const body = (await response.json()) as { data?: TResult; errors?: { message: string }[] };
  if (body.errors?.length) {
    const message = body.errors[0]?.message ?? 'unknown GraphQL error';
    if (body.data == null) throw new GraphQLError(message, body.errors);
    console.warn('GraphQL returned partial data:', body.errors);
  }
  if (body.data == null) throw new GraphQLError('empty response', body.errors ?? []);
  return body.data;
}
